import { forEach, isEmpty, last } from 'lodash';

import { ContractOption } from '@/utils/options/contractOptions';
import {
  StatusItemIssueSeverity,
  StatusItemResponsibility,
} from '@/apps/health_check/types/enums';
import {
  StatusItem,
  StatusItemIssue,
} from '@/apps/health_check/types/IHealthCheck';
import {
  SortField,
  SortOption,
} from '@/apps/health_check/redux/statusItemsSortingSlice';
import { dateComparator, numberComparator } from '@/utils/sortingUtils';

const getAllChildren = (
  option: ContractOption,
  parentPath: string[] = []
): string[][] => {
  if (!option.children || option.children.length === 0) {
    return [[...parentPath]];
  }

  const childPaths = option.children.flatMap((child) =>
    getAllChildren(child, [...parentPath, child.value])
  );

  return childPaths;
};

/**
 * Sanitizes and flattens hierarchical contract options into a flat array of value paths.
 *
 * This function processes selected contract options and expands any options that have
 * child elements into their individual leaf node paths. For options without children,
 * the original path is preserved. For options with children, all descendant paths
 * are recursively collected and added to the result.
 *
 * @param values - Array of string arrays representing the current value paths
 *                 corresponding to each selected option
 * @param selectedOptions - Array of arrays containing ContractOption objects
 *                         representing the hierarchical structure of selected contracts
 *
 * @returns A flattened array of string arrays where each inner array represents
 *          a complete path to a leaf node in the contract hierarchy
 *
 * @example
 * ```typescript
 * const values = [['contract1'], ['contract2', 'subcontract1']];
 * const selectedOptions = [
 *   [{ value: 'contract1', children: [] }],
 *   [{ value: 'contract2' }, { value: 'subcontract1', children: [
 *     { value: 'leaf1' },
 *     { value: 'leaf2' }
 *   ]}]
 * ];
 *
 * const result = sanitizeContractOptions(values, selectedOptions);
 * // Returns: [['contract1'], ['contract2', 'subcontract1', 'leaf1'], ['contract2', 'subcontract1', 'leaf2']]
 * ```
 */
export const flattenContractOptions = (
  values: string[][],
  selectedOptions: ContractOption[][]
) => {
  const newValues: string[][] = [];

  forEach(selectedOptions, (option, index) => {
    const fullPath = values[index];
    const currentOption = last(option);

    if (!fullPath || !currentOption) {
      return;
    }

    if (!currentOption.children || isEmpty(currentOption.children)) {
      newValues.push(fullPath);
      return;
    }

    forEach(currentOption.children, (child) => {
      forEach(
        getAllChildren(child, [...fullPath, child.value]),
        (childPath) => {
          newValues.push(childPath);
        }
      );
    });
  });

  return newValues;
};

type FaultAlarmOption = {
  label: string;
  value: string;
  children?: FaultAlarmOption[];
};

/**
 * Flattens fault alarm options by extracting leaf node values and labels.
 * Expands parent selections to show all individual leaf nodes.
 *
 * @param values - Array of string arrays representing the current value paths
 * @param selectedOptions - Array of arrays containing FaultAlarmOption objects
 *
 * @returns A flattened array of string arrays where each path includes the alarm ID and display name
 */
export const flattenFaultAlarmOptions = (
  values: string[][],
  selectedOptions: FaultAlarmOption[][]
) => {
  const newValues: string[][] = [];

  const expandChildren = (
    option: FaultAlarmOption,
    basePath: string[]
  ): void => {
    if (!option.children || option.children.length === 0) {
      // This is a leaf node (actual fault alarm)
      // basePath already includes the alarm ID, just add the label
      newValues.push([...basePath, option.label]);
      return;
    }

    // This is a parent node, recursively expand all children
    forEach(option.children, (child) => {
      expandChildren(child, [...basePath, child.value]);
    });
  };

  forEach(selectedOptions, (options, index) => {
    const fullPath = values[index];

    if (!fullPath || !options || options.length === 0) {
      return;
    }

    // Get the last selected option in the path
    const currentOption = last(options) as FaultAlarmOption;

    if (!currentOption) {
      return;
    }

    // Start expanding from the current selection
    // The fullPath already includes the value up to this point
    expandChildren(currentOption, fullPath);
  });

  return newValues;
};

export const isStatusItemCritical = (issues: StatusItemIssue[]) =>
  issues.some((issue) => issue.severity === StatusItemIssueSeverity.High);

export const isStatusItemHealthy = (issues: StatusItemIssue[]) =>
  isEmpty(issues);

export const isStatusItemWarning = (issues: StatusItemIssue[]) =>
  !isStatusItemCritical(issues) && !isStatusItemHealthy(issues);

/** Severity is an ascending scale, so the worst issue is the highest value. */
const highestSeverity = (item: StatusItem) =>
  Math.max(
    0,
    ...(item.statusItemIssues ?? []).map((issue) => issue.severity ?? 0)
  );

/**
 * Departure time is a depot concept, so the Dispatcher view does not offer its
 * sort button. The sort state is shared by all three views, so the field a view
 * offers is also the only thing it may order by: a Time Left sort picked in
 * Fleet or Depot would otherwise stay applied in Dispatcher, where dispatcher
 * items do carry a start time and the button carrying the remove affordance is
 * not rendered for the user to see or clear it.
 */
export const isSortFieldOfferedByView = (
  field: SortField,
  responsibilityId?: StatusItemResponsibility
) =>
  field !== 'departureTime' ||
  responsibilityId !== StatusItemResponsibility.Dispatcher;

/** The part of the shared sort state the given view actually offers. */
export const sortOptionsForView = (
  sortOptions: SortOption[],
  responsibilityId?: StatusItemResponsibility
) =>
  sortOptions.filter((option) =>
    isSortFieldOfferedByView(option.field, responsibilityId)
  );

/**
 * Vehicle numbers, with "no number" ranking below every number.
 *
 * The shared `numberComparator` treats a missing value as larger than any
 * number, so multiplying by the direction parked the unallocated items at the
 * top of a descending list. They belong with the low end instead, following the
 * direction like any other small value: first ascending, last descending.
 */
const compareVehicleNumbers = (a?: number | null, b?: number | null) => {
  // Both forms of "absent" tested together. Comparing `a === b` instead would
  // call null and undefined different and report each as the smaller one - the
  // same asymmetry this ticket set out to remove. Unreachable while
  // `sanitizedStatusItems` yields only `number | undefined`, but the signature
  // invites both.
  const aMissing = a === null || a === undefined;
  const bMissing = b === null || b === undefined;

  if (aMissing && bMissing) {
    return 0;
  }
  if (aMissing) {
    return -1;
  }
  if (bMissing) {
    return 1;
  }
  return a - b;
};

export const sortItems = (
  filteredItems: StatusItem[],
  sortOptions: SortOption[]
) =>
  [...filteredItems].sort((a, b) => {
    // Muted-ness, not the moment the mute expires. Comparing the timestamps
    // made two mutes with different end times each report itself as the
    // earlier one, and the user's sort was never reached for that pair.
    // `isMuted` rather than the timestamp: it is the flag the cards, the
    // filters and the mute button all read, so the order cannot disagree with
    // what the list shows.
    if (a.isMuted !== b.isMuted) {
      return a.isMuted ? -1 : 1;
    }

    // Last sort selected has the highest priority
    const reversedOptions = [...sortOptions].reverse();

    for (const option of reversedOptions) {
      const multiplier = option.direction === 'asc' ? 1 : -1;
      let comparison = 0;

      switch (option.field) {
        // Both comparators rank a missing value instead of doing arithmetic on
        // it: subtracting an absent vehicle number yielded NaN, which sort
        // reads as "equal", so every unallocated item tied with everything
        // while the numbered items did not tie with each other. That is not a
        // total order, and the result came out wrong for the numbered items
        // too.
        case 'vehicleId':
          comparison =
            compareVehicleNumbers(a.vehicleId, b.vehicleId) * multiplier;
          break;

        case 'departureTime':
          // `|| null` keeps an empty start time a missing value rather than an
          // Invalid Date, which would put NaN back into the comparison.
          comparison =
            dateComparator(a.startTime || null, b.startTime || null) *
            multiplier;
          break;

        case 'issues':
          const aHasCritical = a.statusItemIssues?.some(
            (e) => e.severity === StatusItemIssueSeverity.High
          );
          const bHasCritical = b.statusItemIssues?.some(
            (e) => e.severity === StatusItemIssueSeverity.High
          );
          if (aHasCritical !== bHasCritical) {
            comparison = (aHasCritical ? 1 : -1) * multiplier; // Status items with critical issues have the highest priority
          } else {
            comparison =
              ((a.statusItemIssues?.length ?? 0) -
                (b.statusItemIssues?.length ?? 0)) *
              multiplier; // Status items with more issues have higher priority
          }
          break;
      }

      if (comparison !== 0) {
        return comparison;
      }
    }

    // Without a tie-break, items the active sorts cannot separate keep whatever
    // order the API returned. That order is not stable between refreshes, and
    // the blended Fleet list makes the tied groups larger.
    const severityComparison = highestSeverity(b) - highestSeverity(a);
    if (severityComparison !== 0) {
      return severityComparison;
    }

    // Not `compareVehicleNumbers`: that rule exists to follow the direction the
    // user picked, and this tie-break has none - it orders a list nobody ranked
    // by vehicle. An item with no vehicle stays at the end here, and must still
    // not read as vehicle number 0, which would put it above every real one.
    return numberComparator(a.vehicleId, b.vehicleId);
  });
